import { observer } from 'mobx-react-lite'
import styles from './WorkspaceSelectorView.module.css'
import { Icon } from '@/components/Icon'
import { WorkspaceSelectorViewModel } from '@/features/iam/workspaces/WorkspaceSelectorView/WorkspaceSelectorViewModel'
import { useActivation, useScopedViewModel } from '@/hooks/view-model-hooks'
import { type WorkspaceMembership } from '@/features/iam/workspaces/WorkspaceMembership'
import { Skeleton, SkeletonList } from '@/components/Skeleton'
import { useSkeletonListContext } from '@/components/Skeleton/SkeletonList'
import cx from 'classnames'
import { useTask } from '@/hooks/task-hooks'
import { Spinner } from '@/components/Spinner'
import { Button } from '@/components/Button'
import { TextField, TextFieldSlot } from '@/components/inputs/TextField'
import { useDocumentTitle } from '@/hooks/document-title'
import { GridList, GridListItem } from 'react-aria-components'
import { useId } from 'react'
import { useLightDarkFn } from '@/features/shell/theming/color-scheme-hooks'

export default observer(function WorkspaceSelectorView() {
  const headingId = useId()

  // The view model is not needed outside the scope of this view.
  const vm = useScopedViewModel(
    ({ iam }) =>
      new WorkspaceSelectorViewModel(iam.workspaceStore, iam.session),
  )
  useActivation(vm)

  useDocumentTitle('Select workspace')

  return (
    <div className={styles.root}>
      <h1 id={headingId} className="text-headline-lg pb-md">
        Select a workspace
      </h1>
      <p className="text-body-md-light">
        Please select a workspace to continue.
      </p>

      <div className="py-xl pt-2xl">
        <TextField
          disabled={vm.activate.pending}
          value={vm.searchText}
          type="search"
          placeholder="Search"
          title="Type here to search for workspaces."
          aria-label="Search for workspaces"
          onChange={(e) => vm.setSearchText(e)}
        >
          <TextFieldSlot>
            <Icon name="search" />
          </TextFieldSlot>
        </TextField>
      </div>

      <GridList
        aria-labelledby={headingId}
        className="flex flex-col gap-base"
        renderEmptyState={() => <Empty searching={!!vm.searchText} />}
      >
        {vm.activate.resolved ? (
          vm.filteredWorkspaces.map((workspace) => (
            <WorkspaceItem
              workspace={workspace}
              onSelect={vm.selectWorkspace}
              disabled={vm.selectWorkspace.pending}
              key={workspace.id}
            />
          ))
        ) : (
          <SkeletonList count={5}>
            <WorkspaceItem />
          </SkeletonList>
        )}
      </GridList>
    </div>
  )
})

interface WorkspaceItemProps {
  workspace?: WorkspaceMembership
  onSelect?: (workspace: WorkspaceMembership) => Promise<void>
  disabled?: boolean
}

const WorkspaceItem = observer(function WorkspaceItem({
  workspace,
  onSelect,
  disabled,
}: WorkspaceItemProps) {
  const lightDark = useLightDarkFn()
  const { vary } = useSkeletonListContext()
  const select = useTask.resolved(
    () => workspace && onSelect?.(workspace),
    [onSelect],
  )

  return (
    <GridListItem
      isDisabled={!workspace || disabled}
      onAction={workspace ? select : undefined}
      className={({ isHovered, isFocusVisible }) =>
        cx(
          styles.item,
          !workspace && styles.skeleton,
          isHovered && styles.hover,
          isFocusVisible && styles.focusVisible,
        )
      }
      textValue={workspace?.name ?? 'Placeholder'}
    >
      <div className="p-md">
        {workspace ? (
          <img
            className={styles.workspaceLogo}
            src={lightDark(workspace.iconUrl, workspace.invertedIconUrl)}
            alt={`Logo for ${workspace.name}`}
          />
        ) : (
          <Skeleton block variant="circle" width={36} height={36} />
        )}
      </div>

      <div className="flex-auto">
        <h2 className="text-headline-md">
          {workspace?.name ?? <Skeleton width={vary(280)} />}
        </h2>
        <p className={styles.workspaceDescription}>
          {workspace?.slug ?? <Skeleton width={vary(60)} />}
        </p>
      </div>

      <div className="flex-none">
        {select.pending ? (
          <div className="px-sm">
            <Spinner variant="default" />
          </div>
        ) : (
          workspace && (
            <div className={styles.icon}>
              <Icon name="chevron" />
            </div>
          )
        )}
      </div>
    </GridListItem>
  )
})

function Empty({ searching }: { searching: boolean }) {
  const vm = useScopedViewModel(({ iam }) => ({
    logout: iam.authStore.logout.bind(iam.authStore),
  }))

  return (
    <div className={styles.empty}>
      <h3 className="text-headline-lg pb-base">No workspaces found</h3>
      <p className="text-body-md">
        {searching
          ? 'Try clearing your search filter.'
          : 'Once you join a workspace, it will appear here.'}
      </p>
      {!searching && (
        <p className="pt-lg">
          <Button label="Log out" onPress={vm.logout} variant="text" />
        </p>
      )}
    </div>
  )
}
